import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Call, Device } from '@twilio/voice-sdk';
import { TWILIO_CONNECT_TARGET } from '../constants/voice-call.constants';
import { CallUiState, VoiceCallerContext } from '../models/voice-call.models';

@Injectable({ providedIn: 'root' })
export class TwilioCallService {
  private readonly ngZone = inject(NgZone);

  readonly twilioState = signal<CallUiState>('idle');
  readonly isMuted = signal(false);
  readonly isSpeakerOn = signal(true);
  readonly errorMessage = signal<string | null>(null);

  private device: Device | null = null;
  private activeCall: Call | null = null;
  private remoteAudio: HTMLAudioElement | null = null;

  async startCall(
    token: string,
    sessionId: string,
    caller: VoiceCallerContext,
  ): Promise<void> {
    this.errorMessage.set(null);
    this.twilioState.set('connecting');
    this.isMuted.set(false);
    this.isSpeakerOn.set(true);
    this.remoteAudio = null;

    try {
      this.device = new Device(token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });

      await this.device.register();
      const params: Record<string, string> = {
        To: TWILIO_CONNECT_TARGET,
        session_id: sessionId,
        user_id: caller.userId,
        tenant_name: caller.tenantName,
        signed_in: 'true',
      };
      if (caller.userName) {
        params['user_name'] = caller.userName;
      }
      if (caller.role) {
        params['role'] = caller.role;
        if (caller.permissions?.length) {
          params['permissions'] = JSON.stringify(caller.permissions);
        }
      }
      const call = await this.device.connect({ params });

      this.activeCall = call;
      this.bindCallEvents(call);
    } catch (err) {
      this.twilioState.set('error');
      this.errorMessage.set(this.toMessage(err));
      await this.cleanupDevice();
      throw err;
    }
  }

  endCall(): void {
    this.activeCall?.disconnect();
    this.resetCallState();
    void this.cleanupDevice();
  }

  toggleMute(): void {
    const call = this.activeCall;
    if (!call) {
      return;
    }

    const shouldMute = !call.isMuted();
    call.mute(shouldMute);
    this.setLocalMicEnabled(call, !shouldMute);
    this.ngZone.run(() => this.isMuted.set(call.isMuted()));
  }

  toggleSpeaker(): void {
    const next = !this.isSpeakerOn();
    this.applySpeakerState(next);
    this.ngZone.run(() => this.isSpeakerOn.set(next));
  }

  private bindCallEvents(call: Call): void {
    call.on('accept', () => {
      this.ngZone.run(() => this.twilioState.set('active'));
    });

    call.on('audio', (remoteAudio) => {
      this.remoteAudio = remoteAudio;
      remoteAudio.muted = !this.isSpeakerOn();
    });

    call.on('disconnect', () => {
      this.ngZone.run(() => this.resetCallState());
    });

    call.on('error', (error) => {
      this.ngZone.run(() => {
        this.twilioState.set('error');
        this.errorMessage.set(error.message);
      });
    });

    call.on('mute', (muted) => {
      this.ngZone.run(() => this.isMuted.set(muted));
    });
  }

  private applySpeakerState(speakerOn: boolean): void {
    if (this.remoteAudio) {
      this.remoteAudio.muted = !speakerOn;
      if (speakerOn) {
        void this.remoteAudio.play().catch(() => undefined);
      }
    }
  }

  private setLocalMicEnabled(call: Call, enabled: boolean): void {
    const stream = call.getLocalStream();
    stream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  private resetCallState(): void {
    this.activeCall = null;
    this.remoteAudio = null;
    this.twilioState.set('idle');
    this.isMuted.set(false);
    this.isSpeakerOn.set(true);
  }

  private async cleanupDevice(): Promise<void> {
    if (!this.device) {
      return;
    }

    try {
      this.device.destroy();
    } catch {
      /* ignore */
    }

    this.device = null;
  }

  private toMessage(err: unknown): string {
    return err instanceof Error ? err.message : 'Call failed';
  }
}
